import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import * as BenchlingUtil from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {BenchlingState} from 'redux-stores/reduceApp';

import {ViewState, WorkflowSelector} from './reduceWorkflows';
import ScreenInfo, {Info, ScreenProps} from './ScreenInfo';
import {getWorkflowsBaseUrl} from './util/WorkflowUtil';
import {COLLAPSIBLE_WORKFLOW_SCREENS, WORKFLOW_SCREEN, WorkflowScreen} from './WorkflowConstants';

function mapStateToProps<Screen extends WorkflowScreen>(state: BenchlingState): ConnectedProps<Screen> {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const viewState = workflowSelector.getViewState() as ViewState<Screen>;
  const screenProps = (ScreenInfo[viewState.screen] as Info<Screen>).readFromStore(
    workflowSelector,
    viewState.params
  );
  return {
    viewState,
    screenProps,
  };
}

interface BaseWorkflowsSidepanelProps {
  isExpanded: boolean;
}

interface ConnectedProps<Screen extends WorkflowScreen> {
  viewState: ViewState<Screen>;
  screenProps: ScreenProps<Screen>;
}

type WorkflowsSidepanelProps<Screen extends WorkflowScreen> = BaseWorkflowsSidepanelProps &
  ConnectedProps<Screen>;

class WorkflowsSidepanel<Screen extends WorkflowScreen> extends React.Component<
  WorkflowsSidepanelProps<Screen>
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    isExpanded: PropTypes.bool.isRequired,

    // Provided by connect
    viewState: PropTypes.shape({
      screen: PropTypes.string.isRequired,
      // When we move this to TypeScript it will be worth refining the type
      params: PropTypes.object,
    }).isRequired,
    screenProps: PropTypes.object.isRequired,
  };

  override componentDidMount(): void {
    const {screen, params} = this.props.viewState;
    (ScreenInfo[screen] as Info<Screen>).fetch(params);
    this._goToScreen(screen);
  }

  override componentDidUpdate(prevProps: WorkflowsSidepanelProps<WorkflowScreen>): void {
    const {screen: oldScreen, params: oldParams} = prevProps.viewState;
    const {screen: newScreen, params: newParams} = this.props.viewState;

    if (
      oldScreen !== newScreen ||
      this.getDocumentURL({screen: oldScreen, params: oldParams}) !==
        this.getDocumentURL({screen: newScreen, params: newParams})
    ) {
      (ScreenInfo[newScreen] as Info<Screen>).fetch(newParams);
    }
    this._goToScreen(newScreen);
  }

  getDocumentURL<S extends WorkflowScreen>({screen, params}: ViewState<S>): string {
    let url;
    const baseUrl = getWorkflowsBaseUrl();
    switch (screen) {
      case WORKFLOW_SCREEN.WORKFLOW_DASHBOARD:
        url = baseUrl;
        break;
      case WORKFLOW_SCREEN.WORKFLOW_DASHBOARD_TEMPLATES:
        url = `${baseUrl}/templates`;
        break;
      case WORKFLOW_SCREEN.WORKFLOW_DETAILS:
        url = `${baseUrl}/view`;
        break;
      case WORKFLOW_SCREEN.WORKFLOW_TEMPLATE_VIEW:
        url = `${baseUrl}/templates/view`;
        break;
      default:
        throw new Error(`Unexpected screen: ${screen}`);
    }

    const urlParam = (ScreenInfo[screen] as Info<S>).getUrlParam(params);
    if (urlParam) {
      url = `${url}/${urlParam}`;
    }
    return url;
  }

  _goToScreen(screen: Screen): void {
    const screenInfo = ScreenInfo[screen] as Info<Screen>;

    if (this.props.isExpanded) {
      BenchlingUtil.setWindowTitle(screenInfo.title(this.props.screenProps));
      if (screen !== WORKFLOW_SCREEN.WORKFLOW_DASHBOARD) {
        // In new nav, URL navigation for the workflows listing is handled via SearchURLs.navigateExpandedView
        const url = this.getDocumentURL(this.props.viewState);
        navigateToURL(url, {skipTrigger: true, replace: false});
      }
    }

    if (!COLLAPSIBLE_WORKFLOW_SCREENS.includes(screen) && !this.props.isExpanded) {
      SidepanelActions.expand();
    }
  }

  override render(): JSX.Element {
    const screenPropsWithIsExpanded = {...this.props.screenProps, isExpanded: this.props.isExpanded};
    return (
      <div className="sidepanel">
        {(ScreenInfo[this.props.viewState.screen] as Info<Screen>).render(screenPropsWithIsExpanded)}
      </div>
    );
  }
}

export default connect(mapStateToProps)(WorkflowsSidepanel);
